import React, { useState, Fragment } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useHistory } from "react-router-dom"
import { toast } from "react-toastify"
import { useTranslation } from "react-i18next"
import isEmpty from "validator/lib/isEmpty"
import isMobilePhone from "validator/lib/isMobilePhone"

import {
  delay,
  clearCart,
  getWhatsappLinkPayMessage,
  validateCartContents
} from "utils"
import { showWarningTopup } from "@redux"
import { addAddress, addUser, sendOrder } from "services"
import { Button, CartStockValidationResultModal } from "components"
import classes from "./PopdPlaceOrderButton.module.scss"

const PopdPlaceOrderButton = props => {
  const {
    setFormErrors,
    paymentDetails,
    orderItems,
    orderCombos,
    customerInfo,
    setCustomerInfo,
    customerAddress,
    setCustomerAddress,
    initialFormErrorsState
  } = props

  const [t] = useTranslation()

  const history = useHistory()

  const dispatch = useDispatch()

  const order = useSelector(state => state.order)

  const selectedCashierBranch =
    useSelector(state => state.userAccountInfo.selectedCashierBranch)

  const temporaryOff =
    useSelector(state => state.systemSettings.temporaryOff)

  const [sending, setSending] = useState(false)
  const [problematicItems, setProblematicItems] = useState([])
  const [openCartValidationResultModal, setOpenCartValidationResultModal] = useState(false)

  const validateCart = async () => {
    const problematicItems =
      await validateCartContents(
        order.items,
        order.combos
      )

    if (problematicItems.length === 0) return true

    setProblematicItems(
      problematicItems
    )

    setOpenCartValidationResultModal(true)

    setSending(false)

    return false
  }

  const validate = () => {
    let result = true
    const errors = { ...initialFormErrorsState }

    if (isEmpty(customerInfo.fullName)) {
      errors.fullName = t("نام_کاربر_الزامی_است")
      result = false
    }

    if (isEmpty(customerInfo.mobilePhone)) {
      errors.mobilePhone = t("شماره_موبایل_کاربر_الزامی_است")
      result = false
    } else if (!isMobilePhone(`+98${customerInfo.mobilePhone.substring(1)}`, ["fa-IR"], { strictMode: true })) {
      errors.mobilePhone = t("شماره_موبایل_کاربر_صحیح_نیست")
      result = false
    }

    if (!customerAddress.areaName) {
      errors.areaName = t("روش_ارسال_الزامی_است")
      result = false
    }

    if (isEmpty(customerAddress.address)) {
      errors.address = t("آدرس_سفارش_الزامی_است")
      result = false
    }

    if (isEmpty(customerAddress.city)) {
      errors.city = t("شهر_الزامی_است")
      result = false
    }

    if (isEmpty(customerAddress.state)) {
      errors.state = t("استان_الزامی_است")
      result = false
    }

    if (
      !!customerAddress.recipientPhoneNumber &&
      !isMobilePhone(`+98${customerAddress.recipientPhoneNumber.slice(1)}`, ["fa-IR"], { strictMode: true })
    ) {
      errors.recipientPhoneNumber = t("شماره_موبایل_گیرنده_اشتباه_است")
      result = false
    }

    setFormErrors(errors)

    return result
  }

  const getRequestBody = (address) => {
    const oItems =
      orderItems.map(item => ({
        asComboSet: false,
        optionId: item.optionId,
        foodId: item.foodId,
        quantity: item.quantity,
        systemDiscount: item.systemDiscount * item.quantity,
        specificationItemIds:
          item.specificationItems.map(si => ({
            _id: si._id,
            quantity: si.quantity,
            builderStepKey: si.builderStepKey,
            portion: si.portion
          }))
      }))

    const cItems = []
    orderCombos.forEach((combo, cIndex) =>
      combo.items.forEach(comboItem =>
        cItems.push({
          foodId: comboItem.foodId,
          optionId: comboItem.optionId,
          quantity: comboItem.quantity,
          specificationItemIds: [],
          comboSet: {
            code: combo.code,
            groupNumber: cIndex + 1,
            comboSetItemId: comboItem.comboSetItemId
          }
        })
      )
    )

    const items = [...cItems, ...oItems]

    const data = {
      items,
      branchId: selectedCashierBranch._id,
      isCod: false,
      isPrinted: true,
      couponCodes: [],
      deliveryType: "delivery",
      address,
      areaName: customerAddress.areaName,
      deliveryService: "internal",
      note: paymentDetails.note,
      paymentNote: paymentDetails.paymentNote,
      paymentAttachUrls: paymentDetails.paymentAttachUrls,
    }

    if (isLinkPay) {
      data["paymentType"] =
        selectedCashierBranch.defaultSmsPaymentGateway

      data["paymentLinkMethod"] =
        paymentDetails.linkPayMethod === "SMS" ? "sms" : "whatsapp"
    }

    return data
  }

  const addNewUser = async () => {
    if (!!customerInfo.accountId)
      return customerInfo.accountId

    const account =
      await addUser({
        registeredFromBranchId: selectedCashierBranch._id,
        fullName: customerInfo.fullName.trim(),
        mobilePhone: `+98${customerInfo.mobilePhone.substring(1)}`,
        note: ""
      })

    setCustomerInfo(prev => ({
      ...prev,
      accountId: account._id
    }))

    return account._id
  }

  const addNewAddressForUser = async (accountId) => {
    if (!!customerAddress._id) return customerAddress

    const recipientPhoneNumber =
      !!customerAddress.recipientPhoneNumber.trim()
        ? `+98${customerAddress.recipientPhoneNumber.slice(1)}`
        : ""

    const newAddress =
      await addAddress({
        ...customerAddress,
        zipCode: customerAddress.zipCode || " ",
        recipientPhoneNumber
      },
        { accountId }
      )

    setCustomerAddress(prev => ({
      ...prev,
      _id: newAddress._id
    }))

    return newAddress
  }

  const sendWhatsappLinkPayMessage = async (paymentId, order) => {
    const message =
      getWhatsappLinkPayMessage(order, paymentId)

    const url =
      `https://api.whatsapp.com/send?phone=${order.customerMobilePhone}&text=${encodeURIComponent(message)}`

    window.open(url, "_blank")

    await delay(500)
  }

  const sendLinkPay = async (order, payment) => {
    const paymentLinkMethod =
      paymentDetails.linkPayMethod

    const isWhatsappLinkPay =
      paymentLinkMethod === "whatsapp"

    isWhatsappLinkPay &&
      sendWhatsappLinkPayMessage(
        payment._id,
        order
      )
  }

  const handlePlaceOrder = async () => {
    if (!validate()) return

    setSending(true)

    try {
      const result = await validateCart()

      if (!result) return

      const newUserAccountId = await addNewUser()

      if (!newUserAccountId) {
        setSending(false)

        toast.error(t("سفارش_ثبت_نشد")) 

        return
      }

      const accountId = newUserAccountId

      const address =
        await addNewAddressForUser(accountId)

      const body = getRequestBody(address)

      const { order, payment } =
        await sendOrder(body, { accountId })

      isLinkPay && sendLinkPay(order, payment)

      clearCart()

      history.push("/foods")

      dispatch(
        showWarningTopup(t("سفارش_ثبت_شد"))
      )
    } catch (err) {
      console.log(err)

      toast.error(t("سفارش_با_موفقیت_انجام_نشد"))

      setSending(false)
    }
  }

  const isLinkPay =
    !!paymentDetails.linkPayMethod

  return (
    <Fragment>
      <div className={classes["container"]}>
        <Button
          color="success"
          disabled={temporaryOff}
          loading={sending}
          onClick={handlePlaceOrder}
          className={classes["button"]}
        >
          {isLinkPay ? t("ثبت_سفارش_و_ارسال_لینک_پرداخت") : t("ثبت_سفارش")}
        </Button>
      </div>
      <CartStockValidationResultModal
        problematicItems={problematicItems}
        openCartValidationResultModal={openCartValidationResultModal}
        setOpenCartValidationResultModal={setOpenCartValidationResultModal}
      />
    </Fragment>
  )
}

export default PopdPlaceOrderButton
